import { useState, useCallback, useEffect, useRef, type ReactNode ,useLayoutEffect} from "react";
import type { TreeData, GroupData, ElementItem, SelectedNode, SubJobData } from "./types";
import { RunIcon, MulRunIcon, ResultIcon, CopyIcon, ImportIcon } from "./Icons";
import "./TreeMenu.css";

import {message,Modal} from "antd";
import SearchElement from "../searchElement";
import { setTimeout } from "timers/promises";
import { jobs, simulation, deleteJob, getJob, getSubJobs } from "@/services/common";
import userInfo from "@/Components/user";
import {
  LoadingOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  WarningFilled,
} from "@ant-design/icons";
import dayjs from "dayjs";

const statusColor: any = {
  error: "#f10202",
  done: "#01894c",
  pedding: "#0066cc",
};

/** TreeMenu 组件的 props */
interface TreeMenuProps {
  data: TreeData;
  dataSource:any;
  selectedNode: SelectedNode | null;
  setSelectedNode: (node: SelectedNode | null) => void;
  setSubName?:(newSubName)=>void;
  setCurResultInfo?: any;
  setResultDrawOpen?:any;
  openParamsModal:()=>void;
  handleSelectVariable?:any;
  setSelectedElementData:(node) =>void;
  runPartTask:(node) =>void;
  runGroupTask:(node) =>void;
  flowChartType:any;
  selectTaskData:any;
  setClearAllNodeIconHighVariable:()=>void;
  setClearAllLinkHighVariable:()=>void;
  setClearAllGroupHighVariable:()=>void;
  setCenterElement:(centerElement)=>void;
  setImportDataType:(newImportDataType)=>void;
  runNodes:any;
}

/** 单个操作按钮配置 */
interface ActionConfig {
  key: string;
  icon: ReactNode;
  title: string;
}

/** 工艺段分组（group）右侧操作按钮 */
const GROUP_ACTIONS: ActionConfig[] = [
  { key: "runGroup", icon: <RunIcon />, title: "工艺段组合计算" },
  { key: "resultGroup", icon: <ResultIcon />, title: "查看结果" },
];

/** 单元操作设备子节点右侧操作按钮 */
const ELEMENT_ACTIONS: ActionConfig[] = [
  { key: "runElement", icon: <RunIcon />, title: "运行单元操作" },
  { key: "resultElement", icon: <ResultIcon />, title: "查看单元操作结果" },
  { key: "importElement", icon: <ImportIcon />, title: "导入单元操作输入数据" },
];

/** 流股子节点右侧操作按钮 */
const STREAM_ACTIONS: ActionConfig[] = [
  { key: "importStream", icon: <ImportIcon />, title: "导入流股输入数据" },
];

export default function TreeMenu(
  { 
    data,
    dataSource,
    selectedNode,
    setSelectedNode,
    setSubName,
    setCurResultInfo,
    setResultDrawOpen,
    openParamsModal,
    handleSelectVariable,
    setSelectedElementData,
    runPartTask,
    runGroupTask,
    flowChartType,
    selectTaskData,
    setClearAllNodeIconHighVariable,
    setClearAllLinkHighVariable,
    setClearAllGroupHighVariable,
    setCenterElement,
    setImportDataType,
    runNodes,
  }: TreeMenuProps) 
{
  const [expandedTopLevels, setExpandedTopLevels] = useState<Set<string>>(new Set(["sections"]));
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedSubGroups, setExpandedSubGroups] = useState<Set<string>>(new Set());
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const isInternalRef = useRef(false);
  const allowAutoExpandRef = useRef(false);

  //新变量
  const token = userInfo.access_token;  //调用接口的token
  const [messageApi, contextHolder] = message.useMessage();
  const [selectName, setSelectName] = useState(null); //选择器子组件显示的value

  //其他变量
  const [popupInfo, setPopupInfo] = useState<{
    visible: boolean;
    x: number;
    y: number;
    data?: [];
  }>({ visible: false, x: 0, y: 0, data: [] });
  const popupRef = useRef<HTMLDivElement>(null);
  const [popupStyle, setPopupStyle] = useState<React.CSSProperties>({});
  const [subJobs, setSubJobs] = useState<any>([]);

  // 外部传入 selectedNode 时自动展开对应层级
  useEffect(() => {
    if (!selectedNode) return;
    // if (isInternalRef.current) return;
    if (!allowAutoExpandRef.current) return;

    setExpandedTopLevels((prev) => {
      if (prev.has("sections")) return prev;
      const next = new Set(prev);
      next.add("sections");
      return next;
    });

    const path = selectedNode.path;
    if (!path || path.length === 0) return;

    if (path[0] === "groups" && path.length >= 2) {
      const groupKey = path[1];
      setExpandedGroups(new Set([groupKey]));

      if (path.length >= 4) {
        const subType = path[2];
        if (subType === "elements" && path.length >= 4) {
          const categoryName = path[3];
          const subGroupKey = `${groupKey}::单元操作`;
          setExpandedSubGroups((prev) => {
            const next = new Set(prev);
            next.add(subGroupKey);
            return next;
          });
          setExpandedCategories((prev) => {
            const next = new Set(prev);
            next.add(`${groupKey}::单元操作::${categoryName}`);
            return next;
          });
        } else if (subType === "streams") {
          const subGroupKey = `${groupKey}::流股`;
          setExpandedSubGroups((prev) => {
            const next = new Set(prev);
            next.add(subGroupKey);
            return next;
          });
        }
      }
    }

    allowAutoExpandRef.current = false;
  }, [selectedNode]);

  // 滚动到选中节点
  useEffect(() => {
    if (!selectedNode) return;
    if (isInternalRef.current) {
      isInternalRef.current = false;
      return;
    }
    const raf = requestAnimationFrame(() => {
      const container = document.querySelector(".tree-scroll-area");
      const el = document.querySelector(".node-content.selected");
      if (container && el) {
        const containerRect = container.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const isFullyVisible =
          elRect.top >= containerRect.top && elRect.bottom <= containerRect.bottom;
        if (isFullyVisible) return;
        const scrollTop =
          container.scrollTop +
          elRect.top -
          containerRect.top -
          containerRect.height / 2 +
          elRect.height / 2;
        container.scrollTo({ top: scrollTop, behavior: "smooth" });
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [selectedNode]);

  // 弹出层位置自适应，防止超出屏幕
  useLayoutEffect(() => {
    if (popupInfo.visible && popupRef.current) {
      const rect = popupRef.current.getBoundingClientRect();
      const offset = 10;
      let left = popupInfo.x + offset;
      let top = popupInfo.y + offset;

      if (left + rect.width > window.innerWidth) {
        left = popupInfo.x - rect.width - offset;
      }
      if (top + rect.height > window.innerHeight) {
        top = popupInfo.y - rect.height - offset;
      }
      left = Math.max(offset, left);
      top = Math.max(offset, top);

      setPopupStyle({ left, top });
    }
  }, [popupInfo.visible, popupInfo.x, popupInfo.y]);

  //外部调用选中树节点
  useEffect(() => 
  {
    if(handleSelectVariable.num>0 && handleSelectVariable.node)
    {
      allowAutoExpandRef.current = true;
      handleSelect(handleSelectVariable.node,true);
    }
  }, [handleSelectVariable]);

  const toggleTopLevel = useCallback((key: string) => {
    setExpandedTopLevels((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleGroup = useCallback((groupKey: string) => {
    setExpandedGroups((prev) => {
      const next = new Set<string>();
      if (!prev.has(groupKey)) {
        next.add(groupKey);
      }
      return next;
    });
  }, []);

  const toggleSubGroup = useCallback((key: string) => {
    setExpandedSubGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleCategory = useCallback((key: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  function ActionButtons({
    actions,
    node,
    onBeforeAction,
  }: {
    actions: ActionConfig[];
    node?: SelectedNode;
    onBeforeAction?: () => void;
  }) {
    return (
      <div className="node-actions">
        {actions.map((a) => (
          <button
            key={a.key}
            className="action-btn"
            title={a.title}
            onClick={(e) => {
              e.stopPropagation();
              // onBeforeAction?.();
              console.log(a.key, node);
              buttonHandler(a.key, node,e);
            }}
          >
            {a.icon}
          </button>
        ))}
      </div>
    );
  }

  function NodeContent({
    label,
    hasChildren,
    isExpanded,
    isSelected,
    actions,
    extraContent,
    onClick,
    onDoubleClick,
    isRun,
  }: {
    label: string;
    hasChildren: boolean;
    isExpanded: boolean;
    isSelected: boolean;
    actions?: ReactNode;
    extraContent?: ReactNode;
    onClick: () => void;
    onDoubleClick?: () => void;
    isRun?:boolean;
  }) {
    const timerRef = useRef<number | null>(null);
    
    const handleClick = () => 
    {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      timerRef.current = window.setTimeout(() => 
      {
        onClick?.();
        timerRef.current = null;
      }, 200);
    };

    const handleDoubleClick = () => 
    {
      if (timerRef.current) 
      {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      onDoubleClick?.();
    };

    return (
      <div className={`node-content${isSelected ? " selected" : ""}`} onClick={handleClick} onDoubleClick={handleDoubleClick}>
        {hasChildren ? (
          <span className={`arrow${isExpanded ? " expanded" : ""}`}>▶</span>
        ) : (
          <span className="arrow-placeholder" />
        )}
        {isRun && (
          <span className="calc-badge">
            <CheckCircleFilled style={{ fontSize: "16px", color: `${statusColor.done}` }} />
          </span>
        )}
        <span className="node-label">{label}</span>
        {extraContent}
        {actions}
      </div>
    );
  }

  const buttonHandler = async(key: string, node?: SelectedNode, e?: any) => 
  {
    // console.log(key, node);

    if (key==="runElement")
    {
      if(flowChartType==="task" && selectTaskData)
      {
        Modal.confirm
        ({
          content:"是否确认运行单元操作",
          onOk:() => 
          {
            runPartTask(node);
          },
          okText: "确认",
          cancelText: "取消",
        });
      }
      else
      {
        messageApi.open
        ({
          type: "error",
          content: "请选择计算任务",
        });
      }
    }
    else if(key==="runGroup")
    {
      if(flowChartType==="task" && selectTaskData)
      {
        Modal.confirm
        ({
          content:"是否确认运行工艺段组合计算",
          onOk:() => 
          {
            runGroupTask(node);
          },
          okText: "确认",
          cancelText: "取消",
        });
      }
      else
      {
        messageApi.open
        ({
          type: "error",
          content: "请选择计算任务",
        });
      }
    }
    else if(key==="resultElement")
    {
      if(flowChartType==="task" && selectTaskData)
      {
        let res = await getSubJobs({job_id:selectTaskData.id,name:node.elementName},token);
        if (res.status === 200) 
        {
          setSubJobs(res.data);
          setPopupInfo({ visible: true, x: e.clientX, y: e.clientY });
        } 
        else 
        {
          messageApi.open
          ({
            type: "error",
            content: `计算任务查询失败：${res.status}`,
          });
        }
      }
      else 
      {
        messageApi.open({
          type: "error",
          content: "请选择计算任务",
        });
      }
    }
    else if(key==="resultGroup")
    {
      if(flowChartType==="task" && selectTaskData)
      {
        let res = await getSubJobs({job_id:selectTaskData.id,name:node.groupName},token);
        if (res.status === 200) 
        {
          setSubJobs(res.data);
          setPopupInfo({ visible: true, x: e.clientX, y: e.clientY });
        } 
        else 
        {
          messageApi.open
          ({
            type: "error",
            content: `计算任务查询失败：${res.status}`,
          });
        }
      }
      else 
      {
        messageApi.open({
          type: "error",
          content: "请选择计算任务",
        });
      }
    }
    else if(key==="importElement")
    {
      if(flowChartType==="task" && selectTaskData)
      {
        setImportDataType({type:"node",key:node.itemKey});
      }
      else
      {
        messageApi.open
        ({
          type: "error",
          content: "请选择计算任务",
        });
      }
    }
    else if(key==="importStream")
    {
      if(flowChartType==="task" && selectTaskData)
      {
        setImportDataType({type:"link",key:node.itemKey});
      }
      else
      {
        messageApi.open
        ({
          type: "error",
          content: "请选择计算任务",
        });
      }
    }
  };

  const handleSelect = useCallback((node: SelectedNode,isOpenModal?:boolean) => 
  {
    isInternalRef.current = true;
    setSelectedNode(node);

    //取消全部元件高亮
    setClearAllNodeIconHighVariable();

    //取消全部流股高亮
    setClearAllLinkHighVariable();

    //取消全部分组高亮
    setClearAllGroupHighVariable();

    //居中所选元件/流股/分组
    if(node.type==="element_item"||node.type==="stream_item"||node.type==="group")
    {
      let itemNameVariableName;
      if(node.type==="element_item")
      {
        itemNameVariableName = "elementName";
      }
      else if(node.type==="stream_item")
      {
        itemNameVariableName = "streamName";
      }
      else if(node.type==="group")
      {
        itemNameVariableName = "groupName";
      }
      setCenterElement({name:node[itemNameVariableName],type:node.type});
    }

    //传入选中项参数
    setSelectedElementData(node);

    //打开参数弹窗
    if(isOpenModal)
    {
      if(node.type==="element_item" && node.path.length===5)
      {
        openParamsModal();
      }
      else if(node.type==="stream_item" && node.path.length===4)
      {
        openParamsModal();
      }
      else if(node.type==="group" && node.path.length===2)
      {
        openParamsModal();
      }
    }
   
    //清空选择器选项
    setSelectName(null);

  }, [setSelectedNode]);

  function UnitOperationNode({
    group,
    groupKey,
    isSubGroupExpanded,
    isCategoryExpanded,
    selectedNode,
    toggleSubGroup,
    toggleCategory,
    handleSelect,
  }: {
    group: GroupData;
    groupKey: string;
    isSubGroupExpanded: (key: string) => boolean;
    isCategoryExpanded: (key: string) => boolean;
    selectedNode: SelectedNode | null;
    toggleSubGroup: (key: string) => void;
    toggleCategory: (key: string) => void;
    handleSelect: (node: SelectedNode,isOpenModal?:boolean) => void;
  }) {
    const subGroupKey = `${groupKey}::单元操作`;
    const expanded = isSubGroupExpanded(subGroupKey);
    const selected =
      selectedNode?.type === "element_category" &&
      selectedNode.groupKey === groupKey &&
      selectedNode.path[2] === "elements";

    return (
      <li className="tree-node level-2">
        <NodeContent
          label="单元操作"
          hasChildren={true}
          isExpanded={expanded}
          isSelected={selected}
          onClick={() => {
            toggleSubGroup(subGroupKey);
            handleSelect({
              type: "element_category",
              groupKey,
              groupName: group.name,
              path: ["groups", groupKey, "elements"],
            });
          }}
        />
        <ul className={`tree-list${expanded ? "" : " collapsed"}`}>
          {group.elements.map((el: ElementItem) => {
            const catKey = `${groupKey}::单元操作::${el.elementname}`;
            const catExpanded = isCategoryExpanded(catKey);
            const catSelected =
              selectedNode?.type === "element_category" &&
              selectedNode.groupKey === groupKey &&
              selectedNode.elementCategory === el.elementname;

            return (
              <li key={catKey} className="tree-node level-3">
                <NodeContent
                  label={el.elementname}
                  hasChildren={true}
                  isExpanded={catExpanded}
                  isSelected={catSelected}
                  onClick={() => {
                    toggleCategory(catKey);
                    handleSelect({
                      type: "element_category",
                      groupKey,
                      groupName: group.name,
                      elementCategory: el.elementname,
                      path: ["groups", groupKey, "elements", el.elementname],
                    });
                  }}
                />
                <ul className={`tree-list${catExpanded ? "" : " collapsed"}`}>
                  {el.children.map((child) => {
                    const childSelected =
                      selectedNode?.type === "element_item" &&
                      selectedNode.groupKey === groupKey &&
                      selectedNode.elementCategory === el.elementname &&
                      selectedNode.elementName === child.name;
                      
                    let isRun = false;
                    if(runNodes)
                    {
                      if(runNodes[child.key])
                      {
                        isRun = true;
                      }
                    }
                    return (
                      <li key={`${catKey}::${child.name}`} className="tree-node level-4 leaf-node">
                        <NodeContent
                          label={child.name}
                          hasChildren={false}
                          isExpanded={false}
                          isSelected={childSelected}
                          actions={
                            <ActionButtons
                              actions={ELEMENT_ACTIONS}
                              node={{
                                type: "element_item",
                                groupKey,
                                groupName: group.name,
                                elementCategory: el.elementname,
                                elementName: child.name,
                                itemKey: child.key,
                                path: ["groups", groupKey, "elements", el.elementname, child.name],
                              }}
                              onBeforeAction={() =>
                                handleSelect({
                                  type: "element_item",
                                  groupKey,
                                  groupName: group.name,
                                  elementCategory: el.elementname,
                                  elementName: child.name,
                                  itemKey: child.key,
                                  path: ["groups", groupKey, "elements", el.elementname, child.name],
                                })
                              }
                            />
                          }
                          onClick={() => {
                            handleSelect({
                              type: "element_item",
                              groupKey,
                              groupName: group.name,
                              elementCategory: el.elementname,
                              elementName: child.name,
                              itemKey: child.key,
                              path: ["groups", groupKey, "elements", el.elementname, child.name],
                            });
                          }}
                          onDoubleClick={() => {
                            handleSelect({
                              type: "element_item",
                              groupKey,
                              groupName: group.name,
                              elementCategory: el.elementname,
                              elementName: child.name,
                              itemKey: child.key,
                              path: ["groups", groupKey, "elements", el.elementname, child.name],
                            },true);
                          }}
                          isRun={isRun}
                        />
                      </li>
                    );
                  })}
                </ul>
              </li>
            );
          })}
        </ul>
      </li>
    );
  }

  function StreamNode({
    group,
    groupKey,
    isSubGroupExpanded,
    selectedNode,
    toggleSubGroup,
    handleSelect,
  }: {
    group: GroupData;
    groupKey: string;
    isSubGroupExpanded: (key: string) => boolean;
    selectedNode: SelectedNode | null;
    toggleSubGroup: (key: string) => void;
    handleSelect: (node: SelectedNode,isOpenModal?: boolean) => void;
  }) {
    const subGroupKey = `${groupKey}::流股`;
    const expanded = isSubGroupExpanded(subGroupKey);
    const selected =
      selectedNode?.type === "stream_item" &&
      selectedNode.groupKey === groupKey &&
      selectedNode.path[2] === "streams";

    return (
      <li className="tree-node level-2">
        <NodeContent
          label="流股"
          hasChildren={true}
          isExpanded={expanded}
          isSelected={selected}
          onClick={() => {
            toggleSubGroup(subGroupKey);
            handleSelect({
              type: "stream_item",
              groupKey,
              groupName: group.name,
              path: ["groups", groupKey, "streams"],
            });
          }}
        />
        <ul className={`tree-list${expanded ? "" : " collapsed"}`}>
          {group.streams.map((stream) => {
            const streamSelected =
              selectedNode?.type === "stream_item" &&
              selectedNode.groupKey === groupKey &&
              selectedNode.streamName === stream.name;
            return (
              <li key={`${groupKey}::stream::${stream.name}`} className="tree-node level-3 leaf-node">
                <NodeContent
                  label={stream.name}
                  hasChildren={false}
                  isExpanded={false}
                  isSelected={streamSelected}
                  actions={
                    <ActionButtons
                      actions={STREAM_ACTIONS}
                      node={{
                        type: "stream_item",
                        groupKey,
                        groupName: group.name,
                        streamName: stream.name,
                        itemKey: stream.key,
                        path: ["groups", groupKey, "streams", stream.name],
                      }}
                      onBeforeAction={() =>
                        handleSelect({
                          type: "stream_item",
                          groupKey,
                          groupName: group.name,
                          streamName: stream.name,
                          itemKey: stream.key,
                          path: ["groups", groupKey, "streams", stream.name],
                        })
                      }
                    />
                  }
                  onClick={() => {
                    handleSelect({
                      type: "stream_item",
                      groupKey,
                      groupName: group.name,
                      streamName: stream.name,
                      itemKey: stream.key,
                      path: ["groups", groupKey, "streams", stream.name],
                    });
                  }}
                  onDoubleClick={() => {
                    handleSelect({
                      type: "stream_item",
                      groupKey,
                      groupName: group.name,
                      streamName: stream.name,
                      itemKey: stream.key,
                      path: ["groups", groupKey, "streams", stream.name],
                    },true);
                  }}
                />
              </li>
            );
          })}
        </ul>
      </li>
    );
  }

  const isGroupExpanded = (groupKey: string) => expandedGroups.has(groupKey);
  const isSubGroupExpanded = (key: string) => expandedSubGroups.has(key);
  const isCategoryExpanded = (key: string) => expandedCategories.has(key);

  return (
    <div className="tree-container-new">
      {contextHolder}
      <div className="tree-scroll-area">
        <ul className="tree-list">
          {/* 工艺段 */}
          <li className={`tree-node level-0${expandedTopLevels.has("sections") ? "" : " collapsed"}`}>
            <NodeContent
              label="工艺段"
              hasChildren={true}
              isExpanded={expandedTopLevels.has("sections")}
              isSelected={false}
              extraContent=
              {
                <div 
                  style={{ flex: 1, minWidth: 0 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <SearchElement
                    selectName={selectName}
                    style={{
                      width:"100%",
                      marginLeft: 10,
                      height: 18,
                      borderRadius: 0,
                      fontSize: 12,
                      paddingBottom: 0,
                      paddingTop: 0,
                      color: "#5F5F5F",
                    }}
                    dataSource={dataSource}
                    searchElementChange={(node,val) => 
                    {
                      allowAutoExpandRef.current = true;
                      handleSelect(node);
                      setSelectName(val);
                    }}
                  />
                </div>
              }
              onClick={() => toggleTopLevel("sections")}
            />
            <ul className={`tree-list${expandedTopLevels.has("sections") ? "" : " collapsed"}`}>
              {data.groups.map((group) => {
                const groupKey = String(group.key);
                const groupExpanded = isGroupExpanded(groupKey);
                const groupSelected =
                  selectedNode?.type === "group" && selectedNode.groupKey === groupKey;

                return (
                  <li key={groupKey} className={`tree-node level-1${groupExpanded ? "" : " collapsed"}`}>
                    <NodeContent
                      label={group.name}
                      hasChildren={true}
                      isExpanded={groupExpanded}
                      isSelected={groupSelected}
                      actions={
                        <ActionButtons
                          actions={GROUP_ACTIONS}
                          node={{
                            type: "group",
                            groupKey,
                            groupName: group.name,
                            path: ["groups", groupKey],
                          }}
                          onBeforeAction={() =>
                            handleSelect({
                              type: "group",
                              groupKey,
                              groupName: group.name,
                              path: ["groups", groupKey],
                            })
                          }
                        />
                      }
                      onClick={() => {
                        toggleGroup(groupKey);
                        handleSelect({
                          type: "group",
                          groupKey,
                          groupName: group.name,
                          path: ["groups", groupKey],
                        });
                      }}
                      onDoubleClick={() => {
                        toggleGroup(groupKey);
                        handleSelect({
                          type: "group",
                          groupKey,
                          groupName: group.name,
                          path: ["groups", groupKey],
                        },true);
                      }}
                    />
                    <ul className={`tree-list${groupExpanded ? "" : " collapsed"}`}>
                      <UnitOperationNode
                        group={group}
                        groupKey={groupKey}
                        isSubGroupExpanded={isSubGroupExpanded}
                        isCategoryExpanded={isCategoryExpanded}
                        selectedNode={selectedNode}
                        toggleSubGroup={toggleSubGroup}
                        toggleCategory={toggleCategory}
                        handleSelect={handleSelect}
                      />
                      <StreamNode
                        group={group}
                        groupKey={groupKey}
                        isSubGroupExpanded={isSubGroupExpanded}
                        selectedNode={selectedNode}
                        toggleSubGroup={toggleSubGroup}
                        handleSelect={handleSelect}
                      />
                    </ul>
                  </li>
                );
              })}
            </ul>
          </li>
        </ul>
      </div>

      {/* 组合计算结果固定在底部 */}
      <div className="tree-bottom-fixed">
        <ul className="tree-list">
          <li className={`tree-node level-0${expandedTopLevels.has("results") ? "" : " collapsed"}`}>
            <NodeContent
              label="组合计算结果"
              hasChildren={data.subJobs.length > 0}
              isExpanded={expandedTopLevels.has("results")}
              isSelected={false}
              onClick={() => toggleTopLevel("results")}
            />
            <ul className={`tree-list${expandedTopLevels.has("results") ? "" : " collapsed"}`}>
              {data.subJobs.length > 0 ? (
                data.subJobs.map((sub) => {
                  const subSelected =
                    selectedNode?.type === "subjob" && selectedNode.subJobId === sub.id;
                  return (
                    <li key={sub.id} className="tree-node level-1 leaf-node">
                      <NodeContent
                        label={sub.name}
                        hasChildren={false}
                        isExpanded={false}
                        isSelected={subSelected}
                        actions={null}
                        onClick={async() => {
                          handleSelect({
                            type: "subjob",
                            subJobId: sub.id,
                            subJobName: sub.name,
                            path: ["subJobs", String(sub.id)],
                          });

                          setSubName(sub.name);

                          await setCurResultInfo({
                            id: sub.id,
                            type: "multiJobs",
                          });
                          await setResultDrawOpen(true);
                        }}
                      />
                    </li>
                  );
                })
              ) : (
                <li className="tree-node placeholder">暂无组合计算结果</li>
              )}
            </ul>
          </li>
        </ul>
      </div>

      {popupInfo.visible && (
        <div
          className="click-popup-overlay"
          onClick={() => setPopupInfo({ visible: false, x: 0, y: 0 })}
        >
          <div
            ref={popupRef}
            className="click-popup-content"
            style={popupStyle}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="click-popup-header">
              {/* <span>{selectedNode.item_name}单元计算结果：</span> */}
              <button
                className="click-popup-close"
                onClick={() => setPopupInfo({ visible: false, x: 0, y: 0 })}
              >
                ×
              </button>
            </div>
            <div className="click-popup-body">
              {subJobs.length ? (
                <ul className="subJobsList">
                  {subJobs.map((i: any, index: number) => {
                    return (
                      <li
                        key={index}
                        onClick={async () => {
                          setSubName(i.name);

                          await setCurResultInfo({
                            id: i.id,
                            type: "multiJobs",
                          });
                          await setResultDrawOpen(true);

                          //点击后关闭
                          setPopupInfo({ visible: false, x: 0, y: 0 });
                        }}
                      >
                        {/* <span style={{backgroundColor:`${statusColor[i.status]}`}}>{index + 1}</span> */}
                        <b style={{ color: `${statusColor[i.status]}` }}>
                          {dayjs(i.created_at).add(8, "hours").format("YYYY-MM-DD HH:mm:ss")}
                        </b>
                        <i className={i.status}>
                          {i.status === "done" ? (
                            <CheckCircleFilled
                              style={{ color: `${statusColor[i.status]}`, fontSize: 16 }}
                            />
                          ) : i.status === "error" ? (
                            <WarningFilled
                              style={{ color: `${statusColor[i.status]}`, fontSize: 16 }}
                            />
                          ) : (
                            <LoadingOutlined
                              style={{ color: `${statusColor[i.status]}`, fontSize: 16 }}
                            />
                          )}
                        </i>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p>此单元没有计算结果。</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}